import { z } from "zod";
import {
  DomainModelMetaExtendedSchema,
  DomainModelSchema,
} from "../util/index.ts";
import { TemplatedReportInputSchema } from "./TemplatedReportSchemas.ts";

export const MAX_WORKER_COMPANY_LIMIT = 10;
export const WORKER_NAME_REGEX = /^[a-zA-Z0-9\-_]*$/;
export const PROPERTY_NAME_REGEX = /^[a-zA-Z0-9\-_]*$/;

const LAMBDA_NAME_MAX_LENGTH = 64;
const UUID_LENGTH = 36;
const WORKER_LAMBDA_NAME_PREFIX_LENGTH =
  "workers-".length + UUID_LENGTH + "-".length;

export const MAX_WORKER_NAME_LENGTH =
  LAMBDA_NAME_MAX_LENGTH - WORKER_LAMBDA_NAME_PREFIX_LENGTH;
export const MAX_WORKER_VERSION_DESCRIPTION_LENGTH = 65536;

// Workers
export const WorkerSchema = z
  .object({
    name: z
      .string()
      .regex(
        WORKER_NAME_REGEX,
        "Name must be alphanumeric, hyphens, or underscores"
      )
      .min(1)
      .max(
        MAX_WORKER_NAME_LENGTH,
        `Name must be less than or equal to ${MAX_WORKER_NAME_LENGTH} characters.`
      ),
    company_id: z.string().uuid(),
    repository_id: z.string(),
    machine_user_id: z.string().uuid(),
    description: z.string().max(256).optional(),
  })
  .merge(DomainModelSchema)
  .merge(DomainModelMetaExtendedSchema);
export type WorkerType = z.infer<typeof WorkerSchema>;

export const GetListWorkerFiltersSchema = WorkerSchema.pick({
  name: true,
  description: true,
  created_at: true,
  updated_at: true,
  created_by: true,
  updated_by: true,
}).partial();
export type GetListWorkerFiltersType = z.infer<
  typeof GetListWorkerFiltersSchema
>;

export const CreateWorkerFormSchema = WorkerSchema.pick({
  name: true,
  description: true,
  machine_user_id: true,
});

export type CreateWorkerFormType = z.infer<typeof CreateWorkerFormSchema>;

// Worker Versions
export const InputOutputSchemaPropertyConfigurationSchema =
  TemplatedReportInputSchema;
export type InputOutputSchemaPropertyConfigurationType = z.infer<
  typeof InputOutputSchemaPropertyConfigurationSchema
>;

export const WorkerVersionStatus = {
  PENDING: "pending" as const,
  ACTIVE: "active" as const,
  FAILED: "failed" as const,
  ARCHIVED: "archived" as const,
} as const;
export type WorkerVersionStatusType =
  (typeof WorkerVersionStatus)[keyof typeof WorkerVersionStatus];

export const WorkerVersionSchema = z
  .object({
    worker_id: z.string().uuid(),
    version: z.number().int().nonnegative(),
    description: z
      .string()
      .max(MAX_WORKER_VERSION_DESCRIPTION_LENGTH)
      .nullable()
      .optional(),
    input_schema: z.record(
      z.string(),
      InputOutputSchemaPropertyConfigurationSchema
    ),
    output_schema: z.record(
      z.string(),
      InputOutputSchemaPropertyConfigurationSchema
    ),
    image_digest: z.string().nullable().optional(),
    status: z.nativeEnum(WorkerVersionStatus),
    failed_reason: z.string().nullable().optional(),
  })
  .merge(DomainModelSchema)
  .merge(DomainModelMetaExtendedSchema);
export type WorkerVersionType = z.infer<typeof WorkerVersionSchema>;

export const GetListWorkerVersionFiltersSchema = WorkerVersionSchema.pick({
  worker_id: true,
  version: true,
  status: true,
  created_at: true,
  updated_at: true,
  created_by: true,
  updated_by: true,
})
  .partial()
  .merge(z.object({ name: z.string().optional() }));
export type GetListWorkerVersionFiltersType = z.infer<
  typeof GetListWorkerVersionFiltersSchema
>;

export const CreateOneWorkerVersionRequestBodySchema = WorkerVersionSchema.pick(
  {
    input_schema: true,
    output_schema: true,
  }
).extend({
  description: z.string().max(MAX_WORKER_VERSION_DESCRIPTION_LENGTH).optional(),
});
export type CreateOneWorkerVersionRequestBodyType = z.infer<
  typeof CreateOneWorkerVersionRequestBodySchema
>;
export const UpdateOneWorkerVersionRequestBodySchema = z.object({
  status: z.nativeEnum(WorkerVersionStatus).optional(),
  description: z.string().max(MAX_WORKER_VERSION_DESCRIPTION_LENGTH).optional(),
});
export type UpdateOneWorkerVersionRequestBodyType = z.infer<
  typeof UpdateOneWorkerVersionRequestBodySchema
>;
export const WorkerVersionCredentialsSchema = z.object({
  access_key_id: z.string(),
  secret_access_key: z.string(),
  session_token: z.string(),
  expiration: z.date().or(z.string().datetime()),
  docker_repository_uri: z.string(),
});
export type WorkerVersionCredentials = z.infer<
  typeof WorkerVersionCredentialsSchema
>;
export const CreateOneWorkerVersionResponseSchema = z.object({
  worker_version: WorkerVersionSchema,
  credentials: WorkerVersionCredentialsSchema,
});
export type CreateOneWorkerVersionResponseType = z.infer<
  typeof CreateOneWorkerVersionResponseSchema
>;

// Worker runs
export const WorkerRunStatus = {
  STARTED: "started" as const,
  COMPLETED: "completed" as const,
  FAILED: "failed" as const,
} as const;

export type WorkerRunStatusType =
  (typeof WorkerRunStatus)[keyof typeof WorkerRunStatus];

export const WORKER_RUN_STATUS_VALUES = [
  "started",
  "completed",
  "failed",
] as const;

export const WorkerRunSchema = z
  .object({
    company_id: z.string().uuid(),
    worker_id: z.string().uuid(),
    version: z.number().int().nonnegative(),
    inputs: z.record(z.string(), z.any()),
    outputs: z.record(z.string(), z.any()),
    status: z.nativeEnum(WorkerRunStatus),
    error_message: z.string().nullable().optional(),
    created_at: z.string().datetime(),
    created_by: z.string().uuid(),
    started_at: z.string().datetime(),
    finished_at: z.string().datetime().nullable().optional(),
  })
  .merge(DomainModelSchema);
export type WorkerRunType = z.infer<typeof WorkerRunSchema>;

export const WorkerRunReportSchema = z.object({
  worker_run_id: z.string().uuid(),
  name: z.string().min(1),
  dataset_id: z.string().uuid(),
  transaction_id: z.string().uuid(),
  created_at: z.string().datetime(),
  created_by: z.string().uuid(),
  worker_id: z.string().uuid(),
  version: z.number().int().nonnegative(),
});
export type WorkerRunReportType = z.infer<typeof WorkerRunReportSchema>;

export const CreateOneWorkerRunRequestBodySchema = WorkerRunSchema.pick({
  worker_id: true,
  version: true,
  inputs: true,
});
export type CreateOneWorkerRunRequestBodyType = z.infer<
  typeof CreateOneWorkerRunRequestBodySchema
>;

/** List filters; `status` may be a single value or an array (serialized as `$in` for search). */
export const GetListWorkerRunFiltersSchema = WorkerRunSchema.pick({
  worker_id: true,
  version: true,
  created_at: true,
  created_by: true,
  started_at: true,
  finished_at: true,
})
  .partial()
  .extend({
    /** Filter by run id: one uuid or many (serialized as `$in` for search). */
    id: z
      .union([z.string().uuid(), z.array(z.string().uuid()).min(1)])
      .optional(),
    status: z
      .union([
        z.nativeEnum(WorkerRunStatus),
        z.array(z.nativeEnum(WorkerRunStatus)).min(1),
      ])
      .optional(),
  });
export type GetListWorkerRunFiltersType = z.infer<
  typeof GetListWorkerRunFiltersSchema
>;
